import { useEffect, useRef, useState } from "react";
import { lang } from "src/language/Strings";
export type drawnSignatureType = { x: number; y: number }[][];

export function SignatureInput({
  className,
  canvasRef,
  onClear,
  setSignedTextArea,
  setDrawnCoordinates,
  clearIndex,
}: {
  className?: string;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  onClear?: (redrawText: () => void) => void;
  setDrawnCoordinates: React.Dispatch<React.SetStateAction<drawnSignatureType>>;
  setSignedTextArea?: (
    signedTextArea: [number, number, number, number]
  ) => void;
  clearIndex?: number;
}) {
  const currentStrokeIndexRef = useRef<number>(-1);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Calculate the text area height to prevent drawing on it
    let textAreaHeight = 0;

    // Draw the signing message text above the writable area
    const drawSigningMessage = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // Set text properties
      ctx.font = "14px Arial, sans-serif";
      ctx.fillStyle = "#333333";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";

      // Calculate text position (left-aligned, near the top)
      const textX = 20; // Left margin
      const textY = 20;

      // Split text into lines if it's too long
      const maxWidth = rect.width - 40; // Leave some margin (20px on each side)
      const words = lang.signingMessage.split(" ");
      let line = "";
      let y = textY;

      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;

        if (testWidth > maxWidth && n > 0) {
          ctx.fillText(line, textX, y);
          line = words[n] + " ";
          y += 20; // Line height
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, textX, y);

      // Calculate the total height of the text area (including some padding)
      textAreaHeight = y + 20 + 10; // Last line position + line height + padding

      // Draw a signing line near the bottom of the canvas
      const lineY = rect.height - 40; // 40px from the bottom to leave space for date
      ctx.strokeStyle = "#666666";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]); // Dashed line
      ctx.beginPath();
      ctx.moveTo(textX, lineY);
      ctx.lineTo(rect.width - 20, lineY); // Right margin
      ctx.stroke();
      ctx.setLineDash([]); // Reset line dash

      // // Draw the current date below the signature line
      // const currentDate = new Date().toLocaleDateString();
      // ctx.font = "12px Arial, sans-serif";
      // ctx.fillStyle = "#333333";
      // ctx.textAlign = "left";
      // ctx.textBaseline = "top";
      // const dateY = lineY + 15; // 15px below the signature line
      // ctx.fillText(currentDate, textX, dateY);

      // Don't update textAreaHeight - allow drawing over the signing line
    };

    // Set up high DPI canvas for crisp rendering
    const setupCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // Set the actual canvas size in memory (scaled for high DPI)
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      // Scale the canvas back down using CSS
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";

      // Scale the drawing context so everything draws at the correct size
      ctx.scale(dpr, dpr);

      // Redraw the signing message after canvas setup
      drawSigningMessage();
    };

    // Set up canvas initially and on resize
    setupCanvas();
    window.addEventListener("resize", setupCanvas);

    // Expose the redraw function to parent component
    if (onClear) {
      onClear(drawSigningMessage);
      setDrawnCoordinates([]);
    }

    // Configure canvas for smooth drawing
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 1;

    // Enable anti-aliasing and smooth rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    let drawing = false;

    function pos(e) {
      const rect = canvas?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };

      // Get the actual touch/mouse coordinates
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      // Get device pixel ratio for proper scaling
      const dpr = window.devicePixelRatio || 1;

      // Convert screen coordinates to canvas coordinates
      // Since we already scaled the context by dpr, we just need the CSS coordinates
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      return { x, y };
    }

    function start(e) {
      const p = pos(e);
      // Start a new stroke
      setDrawnCoordinates((prev) => {
        const next = [...prev, [p]];
        currentStrokeIndexRef.current = next.length - 1;
        return next;
      });
      drawing = true;
      ctx?.beginPath();
      ctx?.moveTo(p.x, p.y);
      e.preventDefault();
    }
    function draw(e) {
      if (!drawing) return;
      const p = pos(e);
      // Append point to current stroke
      setDrawnCoordinates((prev) => {
        const idx = currentStrokeIndexRef.current;
        if (idx < 0 || idx >= prev.length) return prev;
        const updated = prev.slice();
        updated[idx] = [...updated[idx], p];
        return updated;
      });

      // ctx?.lineTo(p.x, p.y);
      ctx?.bezierCurveTo(p.x, p.y, p.x, p.y, p.x, p.y);
      ctx?.stroke();
      e.preventDefault();
    }
    function calculateWritableAreaBoundingBox() {
      if (!canvas) return [0, 0, 0, 0] as [number, number, number, number];

      const rect = canvas.getBoundingClientRect();
      const textX = 20; // Left margin (same as in drawSigningMessage)
      const lineY = rect.height - 40; // Signature line position (same as in drawSigningMessage)

      // Return bounding box of the writable area: [x, y, width, height]
      return [
        textX, // x (left margin)
        textAreaHeight, // y (below text area)
        rect.width - 40, // width (canvas width minus margins)
        lineY - textAreaHeight, // height (from text area to signature line)
      ] as [number, number, number, number];
    }

    function end(e) {
      if (drawing) {
        // Add the final point if we have an event (for mouse/touch end)
        if (e) {
          const p = pos(e);
          // Only add if it's different from the last point (avoid duplicates)
          setDrawnCoordinates((prev) => {
            const idx = currentStrokeIndexRef.current;
            if (idx < 0 || idx >= prev.length) return prev;
            const currentStroke = prev[idx];
            const lastPoint = currentStroke[currentStroke.length - 1];
            if (lastPoint && lastPoint.x === p.x && lastPoint.y === p.y) {
              return prev;
            }
            const updated = prev.slice();
            updated[idx] = [...updated[idx], p];
            return updated;
          });

          if (setSignedTextArea) {
            setSignedTextArea(calculateWritableAreaBoundingBox());
          }
        }
      }
      drawing = false;
      currentStrokeIndexRef.current = -1;
    }

    // Mouse events
    canvas?.addEventListener("mousedown", start);
    canvas?.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", (e) => end(e));
    canvas.addEventListener("mouseout", (e) => end(e));

    // Touch events with passive: false to allow preventDefault
    canvas?.addEventListener("touchstart", start, { passive: false });
    canvas?.addEventListener("touchmove", draw, { passive: false });
    canvas.addEventListener("touchend", (e) => end(e), { passive: false });

    // Cleanup function
    return () => {
      window.removeEventListener("resize", setupCanvas);
      canvas?.removeEventListener("mousedown", start);
      canvas?.removeEventListener("mousemove", draw);
      canvas?.removeEventListener("mouseup", (e) => end(e));
      canvas?.removeEventListener("mouseout", (e) => end(e));
      canvas?.removeEventListener("touchstart", start);
      canvas?.removeEventListener("touchmove", draw);
      canvas?.removeEventListener("touchend", (e) => end(e));
    };
  }, [canvasRef.current, clearIndex]);
  // if SignatureImageUrl is set, draw it on the canvas
  // useEffect(() => {
  //   if (signatureImageUrl) {
  //     const canvas = canvasRef.current;
  //     if (!canvas) return;
  //     const ctx = canvas.getContext("2d");
  //     if (!ctx) return;
  //     const img = new Image();
  //     img.src = signatureImageUrl;

  //     img.onload = () => {
  //       ctx.drawImage(
  //         img,
  //         Math.min(
  //           ...drawnCoordinates.map((stroke) =>
  //             Math.min(...stroke.map((point) => point.x))
  //           )
  //         ),
  //         Math.min(
  //           ...drawnCoordinates.map((stroke) =>
  //             Math.min(...stroke.map((point) => point.y))
  //           )
  //         ),
  //         canvas.width / 4,
  //         canvas.height / 4
  //       );
  //     };
  //   }
  // }, [signatureImageUrl, canvasRef.current, drawnCoordinates]);
  return (
    <canvas
      id="pad"
      ref={canvasRef as React.RefObject<HTMLCanvasElement>}
      className={`border-1 border-Grey4 touch-action-none ${className} max-w-[540px]`}
    />
  );
}
